import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCampaign, useCampaignStats, usePublishers } from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { StatsRow } from '@/api/types';
import { PageHeading } from '@/app/PageHeading';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { LineChart, type LineChartDatum } from '@/components/arc/line-chart/line-chart';
import { MetricCard } from '@/components/arc/metric-card/metric-card';
import SegmentedControl from '@/components/arc/segmented-control/segmented-control';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { CampaignStatusBadge } from '@/components/status/StatusBadge';
import { KpiCard } from '@/pages/account/KpiCard';
import { axisLabelAt, periodAxisLabel, periodLabel } from '@/pages/account/chartFormat';
import { fmtMoney, fmtNumber, fmtPct, truncate } from '@/lib/format';
import { useHref } from '@/module/basePath';
import styles from './overlay.module.css';

type BreakdownRow = { [K in keyof StatsRow]: StatsRow[K] } & { name: string };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RANGES = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];
const SERIES = [
  { key: 'impressions', label: 'Impressions' },
  { key: 'viewable', label: 'Viewable', area: false },
  { key: 'clicks', label: 'Clicks', area: false },
];

export function CampaignStatsPage() {
  const { id } = useParams<{ id: string }>();
  const href = useHref();
  const [days, setDays] = useState('30');
  const camp = useCampaign(id);
  const stats = useCampaignStats(id, Number(days));
  const pubs = usePublishers();

  const publisherNames = useMemo(
    () => new Map((pubs.data?.items ?? []).map((p) => [p.id, p.name])),
    [pubs.data],
  );
  const daily = useMemo<LineChartDatum[]>(() => {
    const rows = stats.data?.by_day ?? [];
    return rows.map((r, i) => ({
      key: r.key,
      label: periodLabel(r.key),
      axisLabel: axisLabelAt(i, rows.length) ? periodAxisLabel(r.key) : undefined,
      values: { impressions: r.impressions, viewable: r.viewable, clicks: r.clicks },
    }));
  }, [stats.data]);
  const byPublisher: BreakdownRow[] = (stats.data?.by_publisher ?? []).map((r) => ({
    ...r,
    name: publisherNames.get(r.key) ?? r.key,
  }));
  const byVideo: BreakdownRow[] = (stats.data?.by_video ?? []).map((r) => ({ ...r, name: r.key }));

  const columns = (kind: 'publisher' | 'video'): DataColumn<BreakdownRow>[] => [
    {
      key: 'name',
      label: kind === 'publisher' ? 'Publisher' : 'Video',
      render: (_, r) =>
        kind === 'video' && UUID.test(r.key) ? (
          <Link to={href(`/videos/${r.key}`)} className={styles.link} title={r.key}>
            <code className={styles.keyCode}>{truncate(r.key, 14)}</code>
          </Link>
        ) : (
          <span title={r.name}>{truncate(r.name, 40)}</span>
        ),
    },
    { key: 'impressions', label: 'Impressions', numeric: true, render: (v) => fmtNumber(Number(v)) },
    {
      key: 'viewable',
      label: 'Viewable',
      numeric: true,
      render: (_, r) => `${fmtNumber(r.viewable)} (${fmtPct(r.viewability)})`,
    },
    { key: 'clicks', label: 'Clicks', numeric: true, render: (v) => fmtNumber(Number(v)) },
    { key: 'ctr', label: 'CTR', numeric: true, render: (v) => fmtPct(Number(v), 2) },
    { key: 'spend_usd', label: 'Spend', numeric: true, render: (v) => fmtMoney(Number(v), 2) },
  ];

  const totals = stats.data?.totals;
  const cpm = camp.data?.cpm_usd ?? 0;

  return (
    <main className="page">
      <PageHeading
        above={
          <Link to={href('/overlay/campaigns')} className={styles.backLink}>
            <ArrowLeft size={16} strokeWidth={1.75} aria-hidden="true" /> Campaigns
          </Link>
        }
        title={camp.data?.name ?? 'Campaign'}
        description="Impressions, viewability and clicks reported by the SDK"
        actions={<SegmentedControl label="Range" options={RANGES} value={days} onValueChange={setDays} />}
        below={
          camp.data ? (
            <div className="row-3">
              <CampaignStatusBadge status={camp.data.status} />
              {camp.data.advertiser_name && (
                <span className="text-secondary">{camp.data.advertiser_name}</span>
              )}
              <span className="text-muted">CPM {fmtMoney(camp.data.cpm_usd, 2)}</span>
              <span className="text-muted">
                {camp.data.publisher_ids.length === 0
                  ? 'All publishers'
                  : `${camp.data.publisher_ids.length} ${camp.data.publisher_ids.length === 1 ? 'publisher' : 'publishers'}`}
              </span>
            </div>
          ) : camp.isError ? (
            <Alert tone="danger" title="Campaign did not load">
              {errorMessage(camp.error)}
            </Alert>
          ) : null
        }
      />

      {stats.isError ? (
        <Alert tone="danger" title="Stats did not load">
          {errorMessage(stats.error)}{' '}
          <Button variant="secondary" size="sm" onClick={() => void stats.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : !totals ? (
        <Skeleton lines={6} label="Loading campaign stats" />
      ) : (
        <>
          <div className="kpi-grid" aria-busy={stats.isFetching}>
            <MetricCard label="Impressions" value={totals.impressions} context={`Last ${days} days`} />
            <MetricCard
              label="Viewable"
              value={totals.viewable}
              context={`${fmtPct(totals.viewability)} viewability`}
            />
            <MetricCard
              label="Clicks"
              value={totals.clicks}
              context={`${fmtPct(totals.ctr, 2)} click-through rate`}
            />
            <KpiCard
              label="Click-through rate"
              value={totals.ctr * 100}
              decimals={2}
              suffix="%"
              context="Clicks per impression"
            />
            <MetricCard label="Dismissed" value={totals.dismisses} context="Closed by the viewer" />
            <KpiCard
              label="Spend"
              value={totals.spend_usd}
              prefix="$"
              decimals={2}
              context={`At ${fmtMoney(cpm, 2)} CPM`}
            />
          </div>

          <section className="stack-4" aria-labelledby="daily-heading">
            <h2 id="daily-heading" className={styles.sectionTitle}>
              Daily
            </h2>
            <LineChart
              data={daily}
              series={SERIES}
              label="Daily impressions, viewable impressions and clicks"
              height={260}
              formatValue={(v) => fmtNumber(v)}
              loading={stats.isFetching && daily.length > 0}
              emptyLabel="No events in this range"
              categoryLabel="Day"
            />
          </section>

          <div className={styles.tables}>
            <section className="stack-4" aria-labelledby="by-publisher-heading">
              <h2 id="by-publisher-heading" className={styles.sectionTitle}>
                By publisher
              </h2>
              {byPublisher.length === 0 ? (
                <EmptyState
                  title="No publisher events yet"
                  description="Rows appear once a publisher page requests the campaign manifest."
                />
              ) : (
                <div className="table-scroll">
                  <SortableDataTable<BreakdownRow>
                    rows={byPublisher}
                    columns={columns('publisher')}
                    rowKey="key"
                    caption="Stats by publisher"
                    defaultSort={{ key: 'impressions', direction: 'desc' }}
                    itemName={{ one: 'publisher', other: 'publishers' }}
                  />
                </div>
              )}
            </section>
            <section className="stack-4" aria-labelledby="by-video-heading">
              <h2 id="by-video-heading" className={styles.sectionTitle}>
                By video
              </h2>
              {byVideo.length === 0 ? (
                <EmptyState
                  title="No video events yet"
                  description="Rows appear once the overlay shows on a mapped video."
                />
              ) : (
                <div className="table-scroll">
                  <SortableDataTable<BreakdownRow>
                    rows={byVideo}
                    columns={columns('video')}
                    rowKey="key"
                    caption="Stats by video"
                    defaultSort={{ key: 'impressions', direction: 'desc' }}
                    itemName={{ one: 'video', other: 'videos' }}
                  />
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </main>
  );
}
